import * as ICONS from "@bracket/ui/icons";
import { Box, } from "@bracket/ui/nodes";
import { getTheme, } from "@bracket/ui/themes";
import type { ColorDeclaration, ThemePalette, } from "@bracket/ui/types";
import { type CSSProperties, type MouseEventHandler, } from "react";
import styled from "styled-components";
import { Icon, } from "../Icon";


export declare type ChipProps = {
	/** Icon name from @bracket/ui/icons (optional but at least one of icon or label required) */
	icon?: keyof typeof ICONS;
	/** Text label to display (optional but at least one of icon or label required) */
	label?: string | number;
	/** CSS overrides */
	style?: CSSProperties;
	/** Whether the chip is disabled (prevents interaction) */
	disabled?: boolean;
	/** Whether the chip is inactive (looks disabled but allows interaction) */
	inactive?: boolean;
	/** Click handler - when provided, cursor becomes pointer */
	onClick?: MouseEventHandler<HTMLDivElement>;
	/** Swatch variant for color styling */
	color?: keyof ThemePalette | ColorDeclaration;
	/** Whether the chip should be a perfect circle */
	circular?: boolean;
};



/**
 * Chip component - a compact badge displaying an icon and/or label.
 *
 * At least one of `icon` or `label` must be provided.
 *
 * @example
 * ```tsx
 * <Chip icon="CheckCircle" label="0" />
 * <Chip label="Verified" />
 * <Chip icon="InfoCircle" />
 * ```
 */
export const Chip = ({
	icon,
	label,
	style,
	disabled,
	inactive,
	onClick,
	color = "surface_on",
	circular,
}: ChipProps) => {

	if(!icon && !label) {
		console.warn("Chip: at least one of `icon` or `label` must be provided");
		return null;
	}

	return (
		<ChipContainer style={style} $disabled={disabled} $inactive={inactive} $hasOnClick={!!onClick} $color={color} $circular={circular} onClick={disabled ? undefined : onClick}>
			{icon && <Icon icon={icon} size="1rem" />}
			{label !== undefined && <span>{label}</span>}
		</ChipContainer>
	);
};


const ChipContainer = styled(Box)<{
	$disabled?: boolean;
	$inactive?: boolean;
	$hasOnClick?: boolean;
	$color: keyof ThemePalette | ColorDeclaration;
	$circular?: boolean;
}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: ${({ $circular }) => ($circular ? "0" : "0.5rem")};
	padding: ${({ $circular }) => ($circular ? "0.375rem" : "0.375rem 1rem")};
	border-radius: ${({ $circular }) => ($circular ? "50%" : "999px")};
	${({ $circular }) => ($circular ? "aspect-ratio: 1; min-width: 2rem; min-height: 2rem;" : "")}
	background: ${ () => getTheme().surface_container_low };
	color: ${({ $color, }) => getTheme()[$color as keyof ThemePalette || "surface_on"] };
	white-space: nowrap;
	opacity: ${({ $disabled, $inactive }) => (($disabled || $inactive) ? 0.5 : 1)};
	cursor: ${({ $disabled, $hasOnClick }) => {
		if($disabled) return "not-allowed";
		if($hasOnClick) return "pointer";
		return "default";
	}};
`;
